import ff from '@prosperllc/web-feature-flags';

import { OPTIMIZELY_VENDOR_NAME } from '../constants';
import { createExperimentTracking } from '../data/queries';

import { heapUserId, logError, logExperimentEvent } from './logging';

/**
 * Track conversion and other key events defined by the given Optimizely feature/experiment.
 * Log experiment event with New Relic, Heap, and svc-experiment-tracking if the given
 * feature/experiment is currently active.
 *
 * @param featureName Name of Optimizely rollout or experiment
 * @param group Variation assigned to the user for the given feature/experiment
 * @param event Event name of key action the user takes for the given feature/experiment
 * @param userId Optional, included with svc-experiment-tracking events
 * @param applicationId Optional, included with svc-experiment-tracking events
 */
export const trackOptimizelyEvent = (
  featureName: string,
  group: string,
  event: string,
  userId?: string,
  applicationId?: string
) => {
  if (ff.isRunning(featureName)) {
    // Track event with New Relic and Heap
    logExperimentEvent(featureName, group, event);
    // Track event with svc-experiment-tracking
    try {
      createExperimentTracking(
        featureName,
        group,
        event,
        userId,
        applicationId,
        OPTIMIZELY_VENDOR_NAME,
        heapUserId() || 'anonymous'
      );
    } catch (err: any) {
      const defaultMessage = `Unexpected error creating experiment tracking for ${featureName}`;
      logError(err, {
        errorName: err.name || 'UtilityError',
        errorMessage: err.message || defaultMessage,
        component: 'feature-flags',
        function: 'trackOptimizelyEvent',
      });
    }
  }
};

/**
 * Count user toward total participant volume of the given Optimizely feature/experiment.
 * Use this function to record initial impressions of a live test.
 * Use trackOptimizelyEvent for all other events.
 *
 * @param featureName Name of Optimizely rollout or experiment
 * @param group Variation assigned to the user for the given feature/experiment
 * @param userId Optional, included with svc-experiment-tracking events
 * @param applicationId Optional, included with svc-experiment-tracking events
 */
export const trackOptimizelyStart = (
  featureName: string,
  group: string,
  userId?: string,
  applicationId?: string
) => {
  trackOptimizelyEvent(featureName, group, 'START', userId, applicationId);
};

export const ifFeatureFlagOn = (featureName: string) => {
  const flag = ff.getActiveVariant(featureName);
  return flag === 'enabled';
};

export const ifIntersectionObserverIsSupported = () => {
  try {
    if (
      'IntersectionObserver' in window &&
      'isIntersecting' in window.IntersectionObserverEntry.prototype
    ) {
      return true;
    }
  } catch (e) {
    // IntersectionObserver is NOT supported
  }

  return false;
};

export const getFeatureVariables = (featureName: string) =>
  ff &&
  ff.getRolloutFeatureVariables &&
  ff.getRolloutFeatureVariables(featureName, {});

export const getFeatureVariable = (featureName: string, variableName: string) =>
  ff &&
  ff.getRolloutFeatureVariable &&
  ff.getRolloutFeatureVariable(featureName, variableName, {});

/**
 * Returns the Optimizely variation assigned to the user for the given feature/experiment.
 *
 * @param featureName Name of Optimizely rollout or experiment
 * @returns Variation assigned to the user for the given feature/experiment. If the feature is
 * not currently active in Optimizely (paused, archived, etc), the default value 'Control'
 * is returned.
 */
export const getOptimizelyVariation = (featureName: string) => {
  const group = ff.getActiveVariant(featureName);
  return group || 'Control';
};
